import { SadieVoiceEngine } from "@/types";

// One line of the live transcript. The worker is whoever has the page open.
// "text" is a text Sadie's backend would have sent (send_text; nothing is
// sent), "event" is something the page did, like pushing a note.
export interface SadieVoiceCaption {
  key: string;
  speaker: "worker" | "sadie" | "text" | "event";
  text: string;
}

// What a server event means for the call, beyond the transcript.
export type SadieVoiceSignal =
  // Commands can be sent now: Live's session.started, Realtime's
  // session.created.
  | { type: "ready" }
  // Live's session.closed: the session is over and nothing more will come.
  | { type: "closed" }
  | { type: "error"; message: string };

// Stand-ins for a worker turn with no transcript.
export const NO_WORDS_HEARD = "(no words heard)";
export const TRANSCRIPTION_FAILED = "(transcription failed)";

interface ServerEvent {
  type?: string;
  delta?: string;
  transcript?: string;
  item_id?: string;
  error?: { message?: string; code?: string } | null;
}

/** Adds text to the caption with this key, creating it at the end if new. */
const upsert = (
  captions: SadieVoiceCaption[],
  key: string,
  speaker: SadieVoiceCaption["speaker"],
  text: string,
  replace = false,
): SadieVoiceCaption[] => {
  const index = captions.findIndex((c) => c.key === key);
  if (index === -1) return [...captions, { key, speaker, text }];
  const next = [...captions];
  next[index] = {
    ...next[index],
    text: replace ? text : next[index].text + text,
  };
  return next;
};

// Live transcript deltas carry no item id: a delta joins the last caption
// when the same person is still talking, and starts a new one otherwise.
const appendLive = (
  captions: SadieVoiceCaption[],
  speaker: SadieVoiceCaption["speaker"],
  delta: string,
): SadieVoiceCaption[] => {
  const last = captions.at(-1);
  if (last?.speaker === speaker) {
    return upsert(captions, last.key, speaker, delta);
  }
  return [
    ...captions,
    { key: `live-${String(captions.length)}`, speaker, text: delta },
  ];
};

const errorSignal = (event: ServerEvent): SadieVoiceSignal => ({
  type: "error",
  message:
    event.error?.message ?? event.error?.code ?? "The voice session errored.",
});

const applyLiveEvent = (
  captions: SadieVoiceCaption[],
  event: ServerEvent,
): { captions: SadieVoiceCaption[]; signal?: SadieVoiceSignal } => {
  switch (event.type) {
    case "session.started":
      return { captions, signal: { type: "ready" } };
    case "session.input_transcript.delta":
      return { captions: appendLive(captions, "worker", event.delta ?? "") };
    case "session.output_transcript.delta":
      return { captions: appendLive(captions, "sadie", event.delta ?? "") };
    case "session.closed":
      return { captions, signal: { type: "closed" } };
    case "error":
      return { captions, signal: errorSignal(event) };
    default:
      return { captions };
  }
};

// Realtime keys every caption by its conversation item, so a worker turn
// keeps its place even when its transcription lands after Sadie has started
// answering.
const applyRealtimeEvent = (
  captions: SadieVoiceCaption[],
  event: ServerEvent,
): { captions: SadieVoiceCaption[]; signal?: SadieVoiceSignal } => {
  const key = event.item_id;
  switch (event.type) {
    case "session.created":
      return { captions, signal: { type: "ready" } };
    case "input_audio_buffer.speech_started":
      return key
        ? { captions: upsert(captions, key, "worker", "") }
        : { captions };
    case "conversation.item.input_audio_transcription.delta":
      return key
        ? { captions: upsert(captions, key, "worker", event.delta ?? "") }
        : { captions };
    case "conversation.item.input_audio_transcription.completed":
      // An empty transcript is a turn the model took for speech (echo, room
      // noise) with no words in it. Saying so tells that apart from a
      // transcript still on its way.
      return key
        ? {
            captions: upsert(
              captions,
              key,
              "worker",
              event.transcript?.trim() ? event.transcript : NO_WORDS_HEARD,
              true,
            ),
          }
        : { captions };
    case "conversation.item.input_audio_transcription.failed":
      return key
        ? {
            captions: upsert(
              captions,
              key,
              "worker",
              TRANSCRIPTION_FAILED,
              true,
            ),
            signal: errorSignal(event),
          }
        : { captions, signal: errorSignal(event) };
    case "response.output_audio_transcript.delta":
      return key
        ? { captions: upsert(captions, key, "sadie", event.delta ?? "") }
        : { captions };
    case "response.output_audio_transcript.done":
      return key
        ? {
            captions: upsert(
              captions,
              key,
              "sadie",
              event.transcript ?? "",
              true,
            ),
          }
        : { captions };
    case "error":
      return { captions, signal: errorSignal(event) };
    default:
      return { captions };
  }
};

const applyServerEvent = (
  engine: SadieVoiceEngine,
  captions: SadieVoiceCaption[],
  event: ServerEvent,
): { captions: SadieVoiceCaption[]; signal?: SadieVoiceSignal } =>
  engine === "live"
    ? applyLiveEvent(captions, event)
    : applyRealtimeEvent(captions, event);

// The two engines speak different event dialects; the page only ever sees
// captions and signals.

/** The transcript after one data-channel event. */
const captionsAfter = (
  engine: SadieVoiceEngine,
  captions: SadieVoiceCaption[],
  event: ServerEvent,
): SadieVoiceCaption[] => applyServerEvent(engine, captions, event).captions;

/** What one data-channel event means for the call, if anything. */
const signalOf = (
  engine: SadieVoiceEngine,
  event: ServerEvent,
): SadieVoiceSignal | undefined => applyServerEvent(engine, [], event).signal;

/** A text or page-event line at the end of the transcript. */
const appendLine = (
  captions: SadieVoiceCaption[],
  speaker: "text" | "event",
  text: string,
): SadieVoiceCaption[] => [
  ...captions,
  { key: `${speaker}-${String(captions.length)}`, speaker, text },
];

// What the Live Responses backend is doing, from a `response.event`. Keyed by
// delegation, like LiveSidebandService.
export type SadieVoiceBackendEvent =
  | { kind: "started"; key: string }
  | {
      kind: "tool";
      key: string;
      callId: string;
      name: string;
      args: Record<string, unknown>;
    }
  | {
      kind: "ended";
      key: string;
      status: "completed" | "incomplete" | "failed";
      error?: string;
    };

interface ResponseEventEnvelope {
  type?: string;
  delegation_id?: string | null;
  event?: {
    type?: string;
    item?: {
      type?: string;
      call_id?: string;
      name?: string;
      arguments?: string;
    };
    response?: { error?: { message?: string } | null };
  };
}

const backendEventOf = (
  event: ResponseEventEnvelope,
): SadieVoiceBackendEvent | null => {
  if (event.type !== "response.event" || !event.event) return null;
  const key = event.delegation_id ?? "default";
  const inner = event.event;
  switch (inner.type) {
    case "response.created":
      return { kind: "started", key };
    case "response.output_item.done": {
      const item = inner.item;
      if (item?.type !== "function_call" || !item.call_id || !item.name) {
        return null;
      }
      let args: Record<string, unknown> = {};
      try {
        args = JSON.parse(item.arguments ?? "{}") as Record<string, unknown>;
      } catch {
        // A tool with unreadable arguments still gets an answer.
      }
      return { kind: "tool", key, callId: item.call_id, name: item.name, args };
    }
    case "response.completed":
    case "response.incomplete":
    case "response.failed":
      return {
        kind: "ended",
        key,
        status: inner.type.replace("response.", "") as
          | "completed"
          | "incomplete"
          | "failed",
        ...(inner.response?.error?.message
          ? { error: inner.response.error.message }
          : undefined),
      };
    default:
      return null;
  }
};

const SPEAKER_LABEL: Record<SadieVoiceCaption["speaker"], string | null> = {
  worker: "Worker",
  sadie: "Sadie",
  text: "Sadie (text message)",
  event: null,
};

/** The call as "Speaker: text" lines, for the post-call prompt. */
const transcriptOf = (captions: SadieVoiceCaption[]): string =>
  captions
    .flatMap((c) => {
      const label = SPEAKER_LABEL[c.speaker];
      return label && c.text.trim() ? [`${label}: ${c.text.trim()}`] : [];
    })
    .join("\n");

export const sadieVoiceSimulator = {
  appendLine,
  backendEventOf,
  captionsAfter,
  signalOf,
  transcriptOf,
};
